import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon } from '@heroicons/react/24/solid';
import MobileLayout from '../../components/MobileLayout';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface UserProfile {
  gender?: string;
  sexualPreference?: string;
}

const UserPreferences: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [profile, setProfile] = useState<UserProfile>({});
  const [selectedGender, setSelectedGender] = useState<string>('');
  const [selectedSexualPreference, setSelectedSexualPreference] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  const genderOptions = [
    { value: 'man', label: t('preferences.genders.man', 'Man') },
    { value: 'woman', label: t('preferences.genders.woman', 'Woman') },
    { value: 'non-binary', label: t('preferences.genders.nonBinary', 'Non-binary') },
  ];

  const sexualPreferenceOptions = [
    { value: 'straight', label: t('preferences.sexualPreferences.straight', 'Straight') },
    { value: 'gay', label: t('preferences.sexualPreferences.gay', 'Gay') },
    { value: 'bisexual', label: t('preferences.sexualPreferences.bisexual', 'Bisexual') },
    { value: 'pansexual', label: t('preferences.sexualPreferences.pansexual', 'Pansexual') },
    { value: 'asexual', label: t('preferences.sexualPreferences.asexual', 'Asexual') },
  ];

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get('/users/profile');
      setProfile(response.data);
      setSelectedGender(response.data.gender || '');
      setSelectedSexualPreference(response.data.sexualPreference || '');
    } catch (err) {
      console.error('Failed to load profile:', err);
      toast.error(t('errors.loadProfile', 'Failed to load profile'));
    } finally {
      setIsFetching(false);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await axios.put('/users/preferences', {
        gender: selectedGender || null,
        sexualPreference: selectedSexualPreference || null,
      });

      toast.success(t('success.saved', 'Settings saved successfully'));
      navigate('/profile');
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('errors.unknown', 'An error occurred'));
    } finally {
      setIsLoading(false);
    }
  };

  const hasChanges =
    selectedGender !== (profile.gender || '') ||
    selectedSexualPreference !== (profile.sexualPreference || '');

  if (isFetching) {
    return (
      <MobileLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-light-text-secondary dark:text-dark-text-secondary">
            {t('common.loading', 'Loading...')}
          </div>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-light-border dark:border-dark-border">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/profile')}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-light-surface dark:hover:bg-dark-surface"
            >
              <ArrowLeftIcon className="w-5 h-5 text-light-text dark:text-dark-text" />
            </button>
            <h1 className="ml-4 text-xl font-semibold text-light-text dark:text-dark-text">
              {t('preferences.title', 'Gender & Sexual Orientation')}
            </h1>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="py-2">
            {/* Gender Section */}
            <div className="mb-6">
              <div className="px-6 py-3 bg-light-surface/50 dark:bg-dark-surface/50">
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                  {t('preferences.gender', 'Gender')}
                </span>
              </div>
              <div className="bg-light-bg dark:bg-dark-bg">
                {genderOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSelectedGender(option.value)}
                    disabled={isLoading}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-light-surface dark:hover:bg-dark-surface transition-colors disabled:opacity-50"
                  >
                    <span className="text-light-text dark:text-dark-text">{option.label}</span>
                    {selectedGender === option.value && (
                      <CheckIcon className="w-5 h-5 text-primary-500" />
                    )}
                  </button>
                ))}
                <button
                  onClick={() => setSelectedGender('')}
                  disabled={isLoading}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-light-surface dark:hover:bg-dark-surface transition-colors disabled:opacity-50 border-t border-light-border dark:border-dark-border"
                >
                  <span className="text-light-text-secondary dark:text-dark-text-secondary italic">
                    {t('preferences.notSet', 'Not set')}
                  </span>
                  {selectedGender === '' && <CheckIcon className="w-5 h-5 text-primary-500" />}
                </button>
              </div>
            </div>

            {/* Sexual Preference Section */}
            <div className="mb-6">
              <div className="px-6 py-3 bg-light-surface/50 dark:bg-dark-surface/50">
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                  {t('preferences.sexualOrientation', 'Sexual Orientation')}
                </span>
              </div>
              <div className="bg-light-bg dark:bg-dark-bg">
                {sexualPreferenceOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSelectedSexualPreference(option.value)}
                    disabled={isLoading}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-light-surface dark:hover:bg-dark-surface transition-colors disabled:opacity-50"
                  >
                    <span className="text-light-text dark:text-dark-text">{option.label}</span>
                    {selectedSexualPreference === option.value && (
                      <CheckIcon className="w-5 h-5 text-primary-500" />
                    )}
                  </button>
                ))}
                <button
                  onClick={() => setSelectedSexualPreference('')}
                  disabled={isLoading}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-light-surface dark:hover:bg-dark-surface transition-colors disabled:opacity-50 border-t border-light-border dark:border-dark-border"
                >
                  <span className="text-light-text-secondary dark:text-dark-text-secondary italic">
                    {t('preferences.notSet', 'Not set')}
                  </span>
                  {selectedSexualPreference === '' && (
                    <CheckIcon className="w-5 h-5 text-primary-500" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        {hasChanges && (
          <div className="p-4 border-t border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg">
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
            >
              {isLoading
                ? t('common.saving', 'Saving...')
                : t('common.saveChanges', 'Save Changes')}
            </button>
          </div>
        )}
      </div>
    </MobileLayout>
  );
};

export default UserPreferences;
